import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { KnowledgeEntry, KnowledgeKind, KnowledgeOverview } from '@contracts/knowledge';
import {
  useKnowledge,
  useKnowledgeActions,
  useKnowledgeDeclined,
  useKnowledgeEntry,
  useDiscussionActions,
  useDiscussions,
} from '../api/queries';
import type { KnowledgeTap } from '../api/queries';
import {
  Broken,
  Button,
  Card,
  Empty,
  MenuButton,
  Pill,
  SkeletonRows,
  useNotices,
  Icon,
} from '../ui';
import { dayLabel, duration, since } from '../lib/time';
import { useEffect, useRef } from 'react';
import { AskBox } from './Answer';
import { useTab, useUrlParam } from '../lib/url-tab';

/**
 * Boards 11 and 20 — what stuck, maintained by the meetings, never by hand.
 *
 * Proposed (said in more than one meeting — never on a single mention) waits
 * for one tap: Keep, or Not worth keeping, which is sticky. A contradiction
 * is the third post-state question — Replace · Keep both · No change. What we
 * know is grouped by what it is about; a row opens to the lines behind it,
 * what it replaced, and what was rejected. Not heard lately: Still true, or
 * Retire. Day one says what the page fills from and when.
 */
export function Knowledge(): ReactElement {
  const [params] = useSearchParams();
  const space = params.get('space');
  const q = useKnowledge(space ? { space } : {}, { poll: true });
  const counts = q.data?.counts;
  return (
    <div className="page stack-lg">
      <div className="page-head" style={{ alignItems: 'flex-start' }}>
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">What we know</h1>
          <p className="mono">
            {counts
              ? `${counts.statements} ${counts.statements === 1 ? 'statement' : 'statements'} · ${counts.proposed} proposed · ${counts.quiet} not heard lately`
              : 'what stuck — maintained by the meetings, never by hand'}
          </p>
        </div>
        <AskBox scope="all" scopeId={null} placeholder="Ask everything we know…" hotkey />
      </div>
      {q.isPending ? (
        <SkeletonRows rows={5} />
      ) : q.isError ? (
        <Card flush>
          <Broken what="Knowledge" retry={() => void q.refetch()} />
        </Card>
      ) : (
        <Loaded data={q.data} />
      )}
    </div>
  );
}

function Loaded({ data }: { data: KnowledgeOverview }): ReactElement {
  const { readEverything } = useKnowledgeActions();
  const { show } = useNotices();
  const [kind, setKind] = useTab<'all' | KnowledgeKind>(
    'kind',
    ['all', 'decision', 'term', 'fact'],
    'all',
  );
  const [declined, setDeclined] = useUrlParam('declined');
  const showDeclined = declined === '1';
  const setShowDeclined = (on: boolean) => setDeclined(on ? '1' : null);
  const counts = data.counts;
  const nothingYet = counts.statements + counts.proposed + counts.quiet === 0;
  const read = data.reading;
  const found = counts.statements + counts.proposed + counts.quiet;
  // The finish screen: shown once, when a backfill this page watched has read the last meeting.
  const wasReading = useRef(false);
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    if (read.unread > 0 && read.unread < read.total) wasReading.current = true;
    else if (read.unread === 0 && wasReading.current) {
      wasReading.current = false;
      setFinished(true);
    }
  }, [read.unread, read.total]);
  const byKind = (kind: KnowledgeKind) =>
    [...data.current, ...data.quiet].filter((e) => e.kind === kind).length;
  const oldest = [...data.current, ...data.quiet, ...data.proposed]
    .map((e) => e.firstSeenAt)
    .sort()[0];

  return (
    <>
      {read.unread > 0 && read.unread < read.total ? (
        <Card
          title="Reading everything you have recorded"
          aside={
            <span className="mono">
              {read.total - read.unread} of {read.total}
            </span>
          }
        >
          <div className="progress" aria-hidden="true">
            <span
              style={{ width: `${Math.round(((read.total - read.unread) / read.total) * 100)}%` }}
            />
          </div>
          <p className="t-secondary" style={{ marginTop: 8 }}>
            Newest first. Everything found so far is already usable, the app works normally while
            this runs, and closing the tab does not stop it.
          </p>
          <p className="eyebrow" style={{ marginTop: 12 }}>
            Found so far · {found} {found === 1 ? 'statement' : 'statements'}
          </p>
        </Card>
      ) : null}

      {finished ? (
        <Card>
          <div className="stack" style={{ gap: 12 }}>
            <h2 className="t-title">
              {read.total} {read.total === 1 ? 'meeting' : 'meetings'} read.
            </h2>
            <div className="figures">
              <span className="figure">
                <span className="n">{byKind('decision')}</span>
                <span className="k">decisions</span>
              </span>
              <span className="figure">
                <span className="n">{byKind('term')}</span>
                <span className="k">terms</span>
              </span>
              <span className="figure">
                <span className="n">{byKind('fact')}</span>
                <span className="k">facts</span>
              </span>
              <span className="figure">
                <span className="n">{oldest ? coveredWords(oldest) : '—'}</span>
                <span className="k">covered</span>
              </span>
            </div>
            <p className="t-secondary" style={{ maxWidth: '65ch' }}>
              Decisions nobody wrote down, in one list. This is the best first impression the
              product will ever make, so it is a screen rather than a notification that disappears.
            </p>
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                variant="primary"
                onClick={() => {
                  setFinished(false);
                  document.getElementById('what-we-know')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Look at what we know
              </Button>
              <Button variant="quiet" onClick={() => setFinished(false)}>
                Not now
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {nothingYet ? (
        <Card flush>
          <Empty
            lead="Nothing has stuck yet."
            action={
              read.unread > 0 ? (
                <Button
                  variant="primary"
                  working={readEverything.isPending ? 'Queuing…' : false}
                  onClick={() =>
                    readEverything.mutate(undefined, {
                      onSuccess: (r) =>
                        show({
                          tone: 'success',
                          text: `Reading ${r.queued} ${r.queued === 1 ? 'meeting' : 'meetings'}, newest first. This page fills itself.`,
                        }),
                      onError: (e) => show({ tone: 'failure', text: e.message }),
                    })
                  }
                >
                  Read everything recorded so far ({read.unread})
                </Button>
              ) : (
                <Link to="/meetings" className="btn btn-sm">
                  See the week
                </Link>
              )
            }
          >
            Knowledge fills itself from your meetings. Nothing is proposed on a single mention, so
            the first statements usually appear in the second week — and there is nothing to write
            here in the meantime.
          </Empty>
          <ul className="t-secondary" style={{ margin: '0 0 16px', paddingLeft: 24 }}>
            <li>
              {read.total} {read.total === 1 ? 'meeting' : 'meetings'} with a transcript so far
            </li>
            <li>
              {counts.seen} {counts.seen === 1 ? 'thing' : 'things'} said once, not yet twice
            </li>
            <li>nothing to do — this page will populate itself</li>
          </ul>
        </Card>
      ) : null}

      {data.proposed.length > 0 ? (
        <Card
          title="Proposed"
          aside={
            <span className="cluster" style={{ gap: 8 }}>
              <Pill>{data.proposed.length}</Pill>
              <span className="t-secondary">
                said in more than one meeting — never proposed on a single mention
              </span>
            </span>
          }
          flush
        >
          <div className="know-list">
            {data.proposed.map((e) => (
              <ProposalRow key={e.id} e={e} />
            ))}
          </div>
        </Card>
      ) : null}

      <TopicsProposed />

      {data.merges.map((g) => (
        <MergeCard key={g.entries.map((e) => e.id).join(':')} entries={g.entries} />
      ))}

      {!nothingYet ? (
        <Card
          id="what-we-know"
          title="What we know"
          aside={
            <div className="chips" role="tablist" aria-label="Kind">
              {(['all', 'decision', 'term', 'fact'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={kind === k}
                  className={`chip ${kind === k ? 'on' : ''}`}
                  onClick={() => setKind(k)}
                >
                  {k === 'all' ? 'everything' : `${k}s`}
                </button>
              ))}
            </div>
          }
          flush
        >
          <Grouped
            entries={data.current.filter((e) => kind === 'all' || e.kind === kind)}
            empty={
              data.current.length === 0
                ? 'Nothing kept yet — the proposals above are waiting for a tap.'
                : `No ${kind}s yet.`
            }
          />
        </Card>
      ) : null}

      {data.quiet.length > 0 ? (
        <Card
          title="Not heard lately"
          aside={
            <span className="t-secondary">
              nothing has mentioned these in ninety days — still true, or retire
            </span>
          }
          flush
        >
          <div className="know-list">
            {data.quiet.map((e) => (
              <KnownRow key={e.id} e={e} quiet />
            ))}
          </div>
        </Card>
      ) : null}

      <Discussions />

      <DeclinedCard open={showDeclined} onToggle={() => setShowDeclined(!showDeclined)} />
    </>
  );
}

/**
 * Three names, one thing (board 20): the app proposes the merge; the losers
 * become aliases of the winner, keeping every source and every count — which
 * is also what makes search find the thing when somebody uses the old name.
 */
function MergeCard({ entries }: { entries: KnowledgeEntry[] }): ReactElement {
  const { merge, distinct } = useKnowledgeActions();
  const { show } = useNotices();
  const [into, setInto] = useState(entries[0]!.id);
  const [chosen, setChosen] = useState<Set<string>>(new Set(entries.map((e) => e.id)));
  const winner = entries.find((e) => e.id === into) ?? entries[0]!;
  const others = entries.filter((e) => e.id !== into && chosen.has(e.id)).map((e) => e.id);
  const busy = merge.isPending || distinct.isPending;
  return (
    <Card
      title="Three names, one thing"
      aside={<span className="t-secondary">the app proposes the merge</span>}
    >
      <div className="stack" style={{ gap: 8 }}>
        {entries.map((e) => (
          <label key={e.id} className="between" style={{ gap: 12, cursor: 'pointer' }}>
            <span className="cluster" style={{ gap: 10 }}>
              <input
                type="checkbox"
                checked={chosen.has(e.id)}
                disabled={e.id === into}
                onChange={(ev) =>
                  setChosen((prev) => {
                    const next = new Set(prev);
                    if (ev.target.checked) next.add(e.id);
                    else next.delete(e.id);
                    return next;
                  })
                }
              />
              <span>{e.statement}</span>
            </span>
            <span className="mono t-secondary cluster" style={{ gap: 8, whiteSpace: 'nowrap' }}>
              {e.sightings} {e.sightings === 1 ? 'mention' : 'mentions'}
              {e.id === into ? (
                <span>· wins</span>
              ) : (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => setInto(e.id)}
                >
                  make this the name
                </button>
              )}
            </span>
          </label>
        ))}
        <div className="cluster" style={{ gap: 8, marginTop: 4 }}>
          <Button
            variant="primary"
            disabled={busy || others.length === 0}
            onClick={() =>
              merge.mutate(
                { into, others },
                {
                  onSuccess: () =>
                    show({
                      tone: 'success',
                      text: `Merged into “${shortName(winner.statement)}”.`,
                    }),
                  onError: (err) => show({ tone: 'failure', text: err.message }),
                },
              )
            }
          >
            Merge into “{shortName(winner.statement)}”
          </Button>
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() =>
              distinct.mutate(
                { ids: entries.map((e) => e.id) },
                {
                  onSuccess: () =>
                    show({
                      tone: 'success',
                      text: 'Kept apart — they will not be proposed as one again.',
                    }),
                  onError: (err) => show({ tone: 'failure', text: err.message }),
                },
              )
            }
          >
            Different things
          </Button>
        </div>
        <p className="t-secondary">
          The losers become aliases, keeping every source and every count — which is also what makes
          search find the thing when somebody uses the old name.
        </p>
      </div>
    </Card>
  );
}

function shortName(text: string): string {
  const m = text.match(/^[“"]([^”"]{2,40})[”"]/);
  if (m) return m[1]!;
  return text.length > 36 ? `${text.slice(0, 33).trim()}…` : text;
}

/**
 * Topics (PRD 30 §3): "These 3 meetings look like one design — write it up?"
 * The app proposes from the data; a person says write it up, or not one
 * thing, which is remembered.
 */
function TopicsProposed(): ReactElement | null {
  const topics = useDiscussions();
  const { writeUp, dismiss } = useDiscussionActions();
  const { show } = useNotices();
  const proposed = topics.data?.proposed ?? [];
  if (proposed.length === 0) return null;
  return (
    <>
      {proposed.map((t) => (
        <Card
          key={t.id}
          title={`These ${t.meetings.length} meetings look like one discussion — “${t.title}”`}
          aside={<span className="mono">{duration(t.seconds)} in total</span>}
        >
          <div className="stack" style={{ gap: 10 }}>
            <div className="cluster" style={{ gap: 8, flexWrap: 'wrap' }}>
              {t.meetings.map((m) => (
                <Link key={m.id} to={`/m/${m.id}`} className="chip">
                  {m.title} · {dayLabel(m.startedAt)}
                </Link>
              ))}
            </div>
            <p className="t-secondary">
              One write-up would carry the final decisions and the rejected options, and replace the
              statements each meeting proposed on its own — so an intermediate state never survives.
            </p>
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                variant="primary"
                disabled={writeUp.isPending || dismiss.isPending}
                onClick={() =>
                  writeUp.mutate(t.id, {
                    onSuccess: () =>
                      show({
                        tone: 'success',
                        text: 'Writing it up — a minute or two; the discussion appears below.',
                      }),
                    onError: (err) => show({ tone: 'failure', text: err.message }),
                  })
                }
              >
                Write it up
              </Button>
              <Button
                variant="quiet"
                disabled={writeUp.isPending || dismiss.isPending}
                onClick={() =>
                  dismiss.mutate(t.id, {
                    onSuccess: () =>
                      show({
                        tone: 'success',
                        text: 'Not one thing — they will not be proposed together again.',
                      }),
                    onError: (err) => show({ tone: 'failure', text: err.message }),
                  })
                }
              >
                Not one thing
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </>
  );
}

/** Written-up discussions, newest first — the ADRs that wrote themselves. */
function Discussions(): ReactElement | null {
  const topics = useDiscussions();
  const list = topics.data?.discussions ?? [];
  if (list.length === 0) return null;
  return (
    <Card
      title="Discussions"
      aside={<span className="t-secondary">several meetings, one record</span>}
      flush
    >
      <div className="rows">
        {list.map((t) => (
          <Link key={t.id} to={`/d/${t.id}`} className="row">
            <span className="row-main">
              <span className="row-title">{t.title}</span>
              <span className="row-sub">
                <span className="mono">
                  {t.meetings.length} meetings · {duration(t.seconds)}
                  {t.wroteUpAt ? ` · written up ${since(t.wroteUpAt)}` : ''}
                </span>
                {t.status === 'open' ? <Pill tone="amber">writing up…</Pill> : null}
                {t.entryCount ? (
                  <Pill>
                    {t.entryCount} {t.entryCount === 1 ? 'statement' : 'statements'}
                  </Pill>
                ) : null}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

/** A proposal, or — when it contradicts what we know — the third post-state question. */
function ProposalRow({ e }: { e: KnowledgeEntry }): ReactElement {
  const { tap } = useKnowledgeActions();
  const { show } = useNotices();
  const act = (t: KnowledgeTap, said: string) =>
    tap.mutate(
      { id: e.id, tap: t },
      {
        onSuccess: () => show({ tone: 'success', text: said }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  const busy = tap.isPending;
  if (e.supersedes) {
    return (
      <div className="know-row third-row">
        <div className="third">
          <span className="eyebrow">Does this change what we know?</span>
          <div className="line old">
            <span className="mono">we know</span>
            <span>{e.supersedes.statement}</span>
            <span />
          </div>
          <div className="line">
            <span className="mono">heard</span>
            <span>
              <KindPill kind={e.kind} /> {e.statement}
            </span>
            <Grade e={e} />
          </div>
          <Sub e={e} />
          <div className="acts">
            <Button variant="primary" onClick={() => act('keep', 'Replaced.')} disabled={busy}>
              Replace
            </Button>
            <Button onClick={() => act('keep-both', 'Kept both.')} disabled={busy}>
              Keep both
            </Button>
            <Button variant="quiet" onClick={() => act('decline', 'No change.')} disabled={busy}>
              No change
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="know-row">
      <KindPill kind={e.kind} />
      <span className="statement">{e.statement}</span>
      <Grade e={e} />
      <Sub e={e} />
      <div className="acts">
        <Button variant="primary" onClick={() => act('keep', 'Kept.')} disabled={busy}>
          Keep
        </Button>
        <Button
          variant="quiet"
          onClick={() => act('decline', 'Not worth keeping — and it will not be proposed again.')}
          disabled={busy}
        >
          Not worth keeping
        </Button>
      </div>
    </div>
  );
}

function Sub({ e }: { e: KnowledgeEntry }): ReactElement {
  const about = subjectWords(e);
  return (
    <div className="sub">
      {about ? (
        <span className="t-secondary">
          about{' '}
          <span className="chip">
            <span className="glyph" aria-hidden="true">
              <Icon name={subjectIcon(e)} size={12} />
            </span>{' '}
            {about}
          </span>
        </span>
      ) : null}
      {e.lastSource ? (
        <Link to={`/m/${e.lastSource.meetingId}`} className="mono">
          {e.lastSource.title} · {weekdayClock(e.lastSource.startedAt)}
        </Link>
      ) : null}
      <span className="mono">
        {e.sightings === 1
          ? `heard once, ${since(e.lastMentionedAt)}`
          : `came up ${e.sightings} times in ${spanWords(e.firstSeenAt, e.lastMentionedAt)}`}
      </span>
    </div>
  );
}

/** What we know, grouped by what it is about. */
function Grouped({ entries, empty }: { entries: KnowledgeEntry[]; empty: string }): ReactElement {
  if (entries.length === 0) {
    return (
      <p className="t-secondary" style={{ padding: '14px 16px' }}>
        {empty}
      </p>
    );
  }
  const groups = new Map<string, KnowledgeEntry[]>();
  for (const e of entries) {
    const key = groupOf(e);
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return (
    <div className="know-list">
      {[...groups.entries()].map(([name, list]) => (
        <div key={name}>
          <div className="subject-head">
            <span className="glyph" aria-hidden="true">
              <Icon name={subjectIcon(list[0]!)} size={12} />
            </span>
            <span>{name}</span>
            <span>{list.length}</span>
          </div>
          {list.map((e) => (
            <KnownRow key={e.id} e={e} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** One thing we know: opens to the lines behind it, what it replaced, what was rejected. */
function KnownRow({ e, quiet = false }: { e: KnowledgeEntry; quiet?: boolean }): ReactElement {
  const [open, setOpen] = useState(false);
  const detail = useKnowledgeEntry(open ? e.id : null);
  const { tap } = useKnowledgeActions();
  const { show } = useNotices();
  const act = (t: KnowledgeTap, said: string) =>
    tap.mutate(
      { id: e.id, tap: t },
      {
        onSuccess: () => show({ tone: 'success', text: said }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  return (
    <div className="know-row">
      <button
        type="button"
        className="know-toggle"
        aria-expanded={open}
        aria-label={open ? 'Close' : 'Open'}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="chevron" size={13} rotate={open ? 90 : 0} />
      </button>
      <span className="statement">
        <KindPill kind={e.kind} /> {e.statement}
      </span>
      <span className="cluster" style={{ gap: 10, whiteSpace: 'nowrap' }}>
        <Grade e={e} />
        <span className="mono t-secondary">
          {e.grade === 'CONFIRMED' ? 'confirmed' : e.grade === 'WRITTEN' ? 'written' : 'heard'}{' '}
          {e.sightings === 1 ? 'once' : `${e.sightings} times`} · {monthOf(e.lastMentionedAt)}
        </span>
        {/* ⚠ On the row, not behind its arrow. The two ways out lived only in
            the opened detail, and Vladimir went looking for them: "i need a
            button to drop something from the knowledge that i decided was
            added by mistake or not actual already" (2026-09-23). They are
            different acts, so two items: a thing that STOPPED being true is
            history; a thing that never was is taken out and not proposed
            again. */}
        {e.canAct ? (
          <MenuButton
            size="sm"
            variant="quiet"
            label="···"
            align="right"
            items={[
              {
                label: 'No longer true',
                sub: 'it was true once — kept in the history',
                onSelect: () => act('retire', 'No longer true — retired.'),
              },
              {
                label: 'Added by mistake',
                sub: 'it was never true — removed, and not proposed again',
                danger: true,
                onSelect: () =>
                  act('decline', 'Removed — it was never true, and it will not be proposed again.'),
              },
            ]}
          />
        ) : null}
      </span>
      {quiet ? (
        <div className="acts">
          <Button
            variant="primary"
            size="sm"
            onClick={() => act('still-true', 'Still true — confirmed today.')}
            disabled={tap.isPending}
          >
            Still true
          </Button>
          <Button
            variant="quiet"
            size="sm"
            onClick={() => act('retire', 'Retired.')}
            disabled={tap.isPending}
          >
            Retire
          </Button>
        </div>
      ) : null}
      {open ? (
        <div className="know-detail">
          {e.detail ? <p className="t-body">{e.detail}</p> : null}
          {detail.isPending ? (
            <SkeletonRows rows={2} />
          ) : detail.isError ? (
            <p className="t-secondary">The lines behind this could not load.</p>
          ) : (
            <>
              {detail.data.sources.map((s) => (
                <div key={s.id} className="stack" style={{ gap: 6 }}>
                  <span className="mono t-secondary">
                    {s.role === 'origin'
                      ? 'first heard'
                      : s.role === 'mention'
                        ? 'came up again'
                        : s.role === 'contradiction'
                          ? 'contradicted'
                          : 'confirmed'}{' '}
                    in{' '}
                    {s.canOpen ? (
                      <Link
                        to={`/m/${s.meetingId}${s.startSegmentId ? `#line-${s.startSegmentId}` : ''}`}
                      >
                        {s.title}
                      </Link>
                    ) : (
                      <span>a meeting you cannot open</span>
                    )}{' '}
                    · {dayLabel(s.startedAt)}
                  </span>
                  {s.lines.length ? (
                    <div className="know-lines">
                      {s.lines.map((l) => (
                        <span key={l.id}>
                          <span className="who">{l.speakerName ?? 'Unknown'}</span> {l.text}
                        </span>
                      ))}
                    </div>
                  ) : s.quote && s.canOpen ? (
                    <div className="know-lines">
                      <span>{s.quote}</span>
                    </div>
                  ) : null}
                </div>
              ))}
              {detail.data.alternatives.length ? (
                <div className="stack" style={{ gap: 4 }}>
                  <span className="mono t-secondary">rejected</span>
                  {detail.data.alternatives.map((a, i) => (
                    <span key={i}>
                      {a.option}
                      {a.whyNot ? <span className="t-secondary"> — {a.whyNot}</span> : null}
                    </span>
                  ))}
                </div>
              ) : null}
              {detail.data.chain.length > 1 ? (
                <div className="stack" style={{ gap: 4 }}>
                  <span className="mono t-secondary">how the thinking changed</span>
                  {detail.data.chain.map((c) => (
                    <span key={c.id} className={c.status === 'current' ? '' : 't-secondary'}>
                      <span className="mono">{monthOf(c.firstSeenAt)}</span>{' '}
                      {c.status === 'current' ? c.statement : <s>{c.statement}</s>}{' '}
                      <span className="mono t-secondary">{c.status}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}
          {e.canAct && !quiet ? (
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                size="sm"
                onClick={() => act('retire', 'No longer true — retired.')}
                disabled={tap.isPending}
              >
                No longer true
              </Button>
              <Button
                size="sm"
                variant="quiet"
                onClick={() =>
                  act('decline', 'Removed — it was never true, and it will not be proposed again.')
                }
                disabled={tap.isPending}
              >
                Added by mistake
              </Button>
              <Link
                to={`/ask?scope=all&q=${encodeURIComponent(`Why ${e.kind === 'decision' ? 'did we decide' : 'do we say'} “${e.statement}”?`)}`}
                className="btn btn-sm btn-quiet"
              >
                Ask about this
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DeclinedCard({ open, onToggle }: { open: boolean; onToggle: () => void }): ReactElement {
  const declined = useKnowledgeDeclined(true);
  const { tap } = useKnowledgeActions();
  const { show } = useNotices();
  const list = declined.data ?? [];
  if (list.length === 0) return <></>;
  return (
    <Card
      title="Not worth keeping"
      aside={
        <button
          type="button"
          className="btn btn-sm btn-quiet"
          onClick={onToggle}
          aria-expanded={open}
        >
          {open ? 'Hide' : `Show ${list.length}`}
        </button>
      }
      flush={open}
    >
      {open ? (
        <div className="know-list">
          {list.map((e) => (
            <div key={e.id} className="know-row">
              <KindPill kind={e.kind} />
              <span className="statement t-secondary">
                {e.statement}{' '}
                {e.declinedAt ? <span className="mono">· {dayLabel(e.declinedAt)}</span> : null}
              </span>
              <Button
                size="sm"
                onClick={() =>
                  tap.mutate(
                    { id: e.id, tap: 'undo-decline' },
                    {
                      onSuccess: () => show({ tone: 'success', text: 'Back among the proposals.' }),
                      onError: (err) => show({ tone: 'failure', text: err.message }),
                    },
                  )
                }
                disabled={tap.isPending}
              >
                Undo
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="t-secondary">
          Declining is permanent — the app never proposes the same thing again, however it is
          phrased — so it gets a permanent escape hatch.
        </p>
      )}
    </Card>
  );
}

// --- pieces

export function KindPill({ kind }: { kind: KnowledgeKind }): ReactElement {
  return <span className={`kind ${kind}`}>{kind}</span>;
}

function Grade({ e }: { e: KnowledgeEntry }): ReactElement {
  const g = e.grade.toLowerCase();
  return <Pill tone={g === 'confirmed' ? 'green' : undefined}>{g}</Pill>;
}

function subjectWords(e: KnowledgeEntry): string | null {
  if (!e.subject) return null;
  if (e.subject.type === 'us') return e.subject.name ?? 'the company';
  return e.subject.name ?? e.subject.key;
}

function groupOf(e: KnowledgeEntry): string {
  if (!e.subject) return 'Everything else';
  if (e.subject.type === 'us') return `${e.subject.name ?? 'Us'} — the company`;
  return e.subject.name ?? e.subject.key ?? 'Everything else';
}

function monthOf(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short' });
}

function subjectIcon(e: KnowledgeEntry): 'space' | 'person' | 'company' {
  if (!e.subject) return 'space';
  return e.subject.type === 'series' ? 'space' : e.subject.type === 'person' ? 'person' : 'company';
}

/** "Wed 09:18" — the weekday and the clock of the meeting it last came from. */
function weekdayClock(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

/** "2 weeks", "a month", "3 days" — how long the sightings span. */
function spanWords(firstIso: string, lastIso: string): string {
  const days = Math.max(1, Math.round((Date.parse(lastIso) - Date.parse(firstIso)) / 86_400_000));
  if (days < 2) return 'a day';
  if (days < 14) return `${days} days`;
  if (days < 56) return `${Math.round(days / 7)} weeks`;
  if (days < 365) {
    const months = Math.round(days / 30);
    return months === 1 ? 'a month' : `${months} months`;
  }
  return `${(days / 365).toFixed(1)} years`;
}

/** "3y 2m", "7m", "5w" — how far back what we know reaches. */
function coveredWords(oldestIso: string): string {
  const days = Math.max(1, Math.round((Date.now() - Date.parse(oldestIso)) / 86_400_000));
  if (days < 60) return `${Math.max(1, Math.round(days / 7))}w`;
  if (days < 365) return `${Math.round(days / 30)}m`;
  const years = Math.floor(days / 365);
  const months = Math.round((days - years * 365) / 30);
  return months ? `${years}y ${months}m` : `${years}y`;
}
